import { useEffect, useState } from 'react';
import Container from '../../components/container/Container';
import { useHistory, useLocation } from 'react-router';
import { filterTuAccounts, getConnectedAccountsCount, getMatchedConnectedAccounts, ISummaryAccounts, mergeByAccountRef } from './utils';
import { mapToSubAccountType, maskAccountNumber } from '../../domain/services/BankAccountModifierService';
import ConnectionSummaryTile from '../../components/endResultStage/ConnectionSummaryTile';
import { useConnectionSummary, useCreditDataAccounts, useSettings } from '../../hooks/queryHooks';
import Spinner from '../../components/spinner/Spinner';
import { useTranslation } from 'react-i18next';

import {
  CreditAccountsFetchStatus,
  useAppState,
  useCreditAccountsState,
  useStepsState
} from '../../hooks/zustandHooks';
import { completeConnection, exitApp } from '../../hooks/utils';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import Modal from '../../components/modal/Modal';
import DiscoveredAccounts from './DiscoveredAccounts';
import { localStorageContainer } from '../../domain/services';
import { CIS_ACCOUNTS } from '../../constants/constants';
import IAccountDto from '../../domain/dto/IAccountDto';

const TuSuccessPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { state: { dataSharingCancelledError, hasValidToken } = {} } =
    useLocation<{ dataSharingCancelledError: boolean; hasValidToken: boolean }>() || {};
  const [isOpen, setIsOpen] = useState(false);
  const { settings } = useSettings();
  const isTuConnectionSuccess = useCreditAccountsState(({ state }) => state.isTuConnectionSuccess);
  const fetchStatus = useCreditAccountsState(({ state }) => state.creditAccountsFetchStatus);
  const bankNameFromUrl = useAppState(state => state.appState.state?.ProviderName);
  const { connectionSummary: { accounts: summaryAccounts = [], providerId } = {}, isFetched: isConnectionSummaryFetched } =
    useConnectionSummary(isTuConnectionSuccess == true);

  const connSummaryAccounts =
    summaryAccounts.length > 0 ? summaryAccounts.map(sa => ({ ...sa, bankNameFromUrl: bankNameFromUrl, providerId: providerId })) : [];
  const [providerFetchState, setProviderFetchState] = useState<{ isLoading: boolean; error: string | null }>({
    isLoading: false,
    error: null
  });
  const enableCisQuery =
    (isTuConnectionSuccess == true && isConnectionSummaryFetched) || dataSharingCancelledError || hasValidToken;

  const { creditData, isCreditDataLoading, isCreditDataError } = useCreditDataAccounts(enableCisQuery);
  const setActiveStep = useStepsState(state => state.setActiveStep);

  useEffect(() => {
    setActiveStep({ stepIndex: isTuConnectionSuccess ? 2 : 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (connSummaryAccounts.length > 0) {
      const storedAccounts = (localStorageContainer.getItemByUserId(CIS_ACCOUNTS) || []) as IAccountDto[];
      if (!storedAccounts || !Array.isArray(storedAccounts)) return;

      const mappedIncoming = connSummaryAccounts.map(csa => ({...csa, bankNameFromUrl: bankNameFromUrl}));
      const accountsToStore = mergeByAccountRef(storedAccounts, mappedIncoming);

      if (storedAccounts.length != accountsToStore.length) {
        localStorageContainer.setItemByUserId(CIS_ACCOUNTS, accountsToStore);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connSummaryAccounts.length]);

  if (isCreditDataError && (!isTuConnectionSuccess || (isConnectionSummaryFetched && connSummaryAccounts.length < 1))) {
    history.push('/bank-select', { hasFailedFetchingTuAccounts: true });
  }

  if (
    providerFetchState.isLoading ||
    isCreditDataLoading ||
    (!isTuConnectionSuccess &&
      !dataSharingCancelledError &&
      !hasValidToken &&
      ![CreditAccountsFetchStatus.Finished, CreditAccountsFetchStatus.Error].includes(fetchStatus))
  ) {
    return (
      <div className="h-full">
        <Spinner text={t('Fetching accounts...')} className="h-full gap-2" />
      </div>
    );
  }

  const { discoveredAccounts, unconfirmedAccounts } = filterTuAccounts(creditData?.accounts || []);

  const matchedDiscoveredAccounts = getMatchedConnectedAccounts(discoveredAccounts, connSummaryAccounts);
  const { connectedAccountsCount, totalAccountsCount } = getConnectedAccountsCount(matchedDiscoveredAccounts);
  const canUseExitBtn = settings?.redirectUrl || window.opener != null;
  return (
    <Container
      primaryBtn={{
        text: 'Add undiscovered account',
        btnType: 'link',
        className: 'px-0!',
        onClick: () => history.push('/bank-select')
      }}
      {...(canUseExitBtn || connectedAccountsCount < totalAccountsCount
        ? {
            secondaryBtn: {
              text: settings?.redirectUrl ? 'Finish & return to partner' : 'Finish',
              className: 'p-0!',
              onClick: () =>
                connectedAccountsCount < totalAccountsCount
                  ? setIsOpen(true)
                  : isTuConnectionSuccess
                  ? completeConnection()
                  : exitApp()
            }
          }
        : {})}
    >
      <h1 className="uppercase text-md sm:text-lg mb-2 font-semi-bold text-primary-2">
        {isTuConnectionSuccess
          ? t(`Account${connSummaryAccounts.length > 1 ? 's' : ''} confirmed`)
          : t('Verification complete')}
      </h1>
      <p className="text-base text-primary-2">
        {isTuConnectionSuccess ? (
          <>
            {t(`Thank you, we now have access to the following account${connectedAccountsCount > 1 ? 's' : ''}.`)}{' '}
            <br />
            {connectedAccountsCount < totalAccountsCount
              ? t(`Please continue connecting your remaining accounts.`)
              : t(`You may now close this tab.`)}
          </>
        ) : (
          <>
            {'You have been successfully verified.'}
            <br />
            <br />
            {t(
              `These are the accounts we’ve located for you via your credit report. Connecting all your accounts gives ${settings?.customerName} the most accurate picture of your finances and keeps your information up to date.`
            )}
          </>
        )}
      </p>
      {dataSharingCancelledError && <p className="text-negative text-base mt-4">Bank data sharing was cancelled.</p>}
      {providerFetchState.error && <p className="text-negative text-base mt-4">{providerFetchState.error}</p>}

      {connSummaryAccounts.length > 0 && (
        <div className="mt-6">
          <span className="text-lg text-gray-800 inline-block">{t(`Connected`)}:</span>
          {connSummaryAccounts.length > 0 &&
            connSummaryAccounts.map((acc, idx) => (
              <div key={idx} className="border-b border-gray-300 rounded-lg mt-1">
                <ConnectionSummaryTile account={acc} bankName={acc?.displayName || bankNameFromUrl} />
              </div>
            ))}
        </div>
      )}

      {Object.keys(matchedDiscoveredAccounts).length > 0 && (
        <div>
          <span className="text-lg text-gray-800 mt-6 mb-4 inline-block">Discovered:</span>
          <div className="flex flex-col gap-10">
            {Object.entries(matchedDiscoveredAccounts).map(([brand, details]) => {
              return (
                <DiscoveredAccounts
                  key={brand}
                  bankName={brand}
                  details={details}
                  setProviderFetchState={setProviderFetchState}
                />
              );
            })}
          </div>
        </div>
      )}
      {Object.keys(unconfirmedAccounts).length > 0 && (
        <div>
          <span className="text-lg text-gray-800 mt-8 inline-block">{t(`Unconfirmed accounts`)}:</span>
          <div>
            {Object.entries(unconfirmedAccounts).map(([brand, details], idx) => {
              return (
                <div key={`${brand}-${idx}`} className="py-2">
                  <h3 className="text-base font-bold text-primary-2">{brand}</h3>
                  {details.accounts.map((a, i) => {
                    if (!a) return null;
                    return (
                      <div key={i} className="flex justify-between mt-2 border-b border-gray-400 pb-2">
                        <div className="flex flex-col text-xs">
                          {a.accountSubType && (
                            <p className="capitalize text-gray-800 text-base p-0! m-0!">
                              {mapToSubAccountType(a.accountSubType)}
                            </p>
                          )}
                          <span className="text-gray-500 text-base font-medium">
                            {maskAccountNumber(a.accountIdentifiers?.find(a => a.value != null)?.value)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
            <PrimaryButton
              onClick={() => history.push('/bank-select')}
              className="max-w-44 sm:max-w-38 mt-4 px-0! py-2!"
            >
              {t('Find & connect')}
            </PrimaryButton>
          </div>
        </div>
      )}
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Are you sure you want to exit?"
        description={`${
          connectedAccountsCount < 1
            ? 'You have not connected any accounts'
            : 'You have not connected all of your discovered accounts'
        }. You can click 'cancel' to continue connecting your account(s), or ${
          canUseExitBtn ? `click 'exit'` : 'you may close this window'
        } to
         ${connectedAccountsCount < 1 ? 'abort and start the process again later' : 'finish'}.`}
        primaryButtonProps={{
          text: 'Cancel',
          className: `${!canUseExitBtn && 'w-auto sm:w-40!'}`,
          onClick: () => setIsOpen(false)
        }}
        {...(canUseExitBtn && {
          secondaryButtonProps: {
            text: 'Exit',
            onClick: () => (isTuConnectionSuccess ? completeConnection() : exitApp())
          }
        })}
      />
    </Container>
  );
};

export default TuSuccessPage;
